import { useState, useEffect, useRef, ChangeEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useUser } from 'context/UserContext'
import { useKeyPress } from 'hooks/useKeyPress'
import { ContinueButton } from 'components/ContinueButton'
import { Heading } from 'components/Heading'
import Input from 'components/Input'
import { UnitSwitcher } from 'components/UnitSwitcher'

interface Props {
  nextRoute: string
}

export const GoalWeight: React.FC<Props> = ({ nextRoute }) => {
  const { t } = useTranslation('translation', {
    useSuspense: false,
  })

  const METRIC_LBS_KEY = 1
  const METRIC_KG_KEY = 2
  const WEIGHT_UNITS = [
    { key: METRIC_LBS_KEY, unit: t('metric_lbs') },
    { key: METRIC_KG_KEY, unit: t('metric_kg') },
  ]
  const { user, setUser } = useUser()
  const genderBasedBackground =
    user.gender === 'm'
      ? 'bg-goal-weight-male bg-center'
      : 'bg-goal-weight-female bg-center'
  const navigate = useNavigate()
  const [canContinue, setCanContinue] = useState(false)
  const [goalWeight, setGoalWeight] = useState('')
  const [weightType, setWeightType] = useState(METRIC_KG_KEY)
  const weightInput = useRef(null)

  const onContinue = () => {
    if (!canContinue) {
      return
    }

    setUser({
      ...user,
      goalWeight,
    })
    setTimeout(() => {
      navigate(nextRoute)
    }, 100)
  }

  const onWeightChange = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value

    if (/(^$|^\d{1,3}$)/.test(input)) {
      setGoalWeight(input)
    }
  }

  useEffect(() => {
    if (goalWeight === '') {
      return
    }

    if (weightType === METRIC_LBS_KEY) {
      setGoalWeight(`${Math.round(parseInt(goalWeight, 10) * 2.205)}`)
    } else {
      setGoalWeight(`${Math.round(parseInt(goalWeight, 10) / 2.205)}`)
    }
  }, [weightType]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setCanContinue(goalWeight.length > 1)
  }, [goalWeight])

  useKeyPress('ENTER', onContinue)

  return (
    <>
      <div
        className={`default-container ${genderBasedBackground} flex flex-col`}>
        <div className="flex h-full w-full flex-col justify-between self-center overflow-auto px-7 pt-7 lg:w-5/6 xl:w-5/12">
          <Heading text={t('goal_weight.headline')} />

          <div className="z-0 mx-auto mt-5 mb-28 w-full">
            <UnitSwitcher
              units={WEIGHT_UNITS}
              selected={weightType}
              onUnitSelected={(unit) => setWeightType(unit)}
            />
            <Input
              placeholder={weightType === METRIC_KG_KEY ? '70' : '115'}
              value={goalWeight}
              id="goal_weight_input"
              onChange={onWeightChange}
              ref={weightInput}
            />
          </div>
        </div>
      </div>
      <ContinueButton onClick={onContinue} disabled={!canContinue} />
    </>
  )
}
